import React, { useEffect, useState } from 'react';
import { parseApiUtc } from '@/utils/apiDate';
import { appDateTime } from '@/utils/appDateFormat';
import { useTranslation } from 'react-i18next';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogBody,
    DialogTitle,
    DialogDescription,
} from '../ui/Dialog';
import { getTermsVersion } from '../../api/General/termsAndPolicies';
import type { TermsVersionView } from '../../models/general';

interface Props {
    version: number | null;
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

export default function VersionViewerModal({ version, open, onOpenChange }: Props) {
    const { t } = useTranslation();
    const [data, setData] = useState<TermsVersionView | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!open || version == null) return;
        let cancelled = false;
        setLoading(true);
        setError(null);
        setData(null);
        getTermsVersion(version)
            .then(res => {
                if (cancelled) return;
                if (res.errors?.length || !res.payload) {
                    setError(res.errors?.[0]?.description ?? t('Failed to load version'));
                } else {
                    setData(res.payload);
                }
            })
            .catch(() => { if (!cancelled) setError(t('Failed to load version')); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [open, version, t]);

    const publishedAt = data?.publishedAt ? appDateTime(parseApiUtc(data.publishedAt)) || null : null;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent size="xl" data-testid="terms-version-modal">
                <DialogHeader>
                    <DialogTitle>
                        {version != null ? t('Terms & Policies — v{{n}}', { n: version }) : t('Terms & Policies')}
                    </DialogTitle>
                    {publishedAt && (
                        <DialogDescription className="mt-1">
                            {t('Published')}: {publishedAt}
                            {data?.publishedByUserName ? ` — ${data.publishedByUserName}` : ''}
                        </DialogDescription>
                    )}
                </DialogHeader>
                <DialogBody>
                    {loading && (
                        <div className="text-sm text-muted-foreground" data-testid="terms-version-loading">
                            {t('Loading…')}
                        </div>
                    )}
                    {error && (
                        <div className="text-sm text-rose-600" data-testid="terms-version-error">
                            {error}
                        </div>
                    )}
                    {data && !loading && !error && (
                        <div
                            data-testid="terms-version-content"
                            className="border border-border rounded-md p-4 bg-gray-50 max-h-[60vh] overflow-y-auto whitespace-pre-wrap text-sm leading-relaxed"
                        >
                            {data.content}
                        </div>
                    )}
                </DialogBody>
            </DialogContent>
        </Dialog>
    );
}
